'use client';

import { useState } from 'react';
import { TradingLog } from './TradingLog';
import { CompletedTrades } from './CompletedTrades';
import { Positions } from './Positions';
import { useLanguage } from '@/contexts/LanguageContext';

type TabType = 'trades' | 'positions' | 'modelchat' | 'info';

interface SidebarProps {
  onClose?: () => void;
}

export function Sidebar({ onClose }: SidebarProps) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState<TabType>('modelchat');

  const tabs = [
    { id: 'trades' as TabType, label: t.sidebar.tabs.completedTrades },
    { id: 'positions' as TabType, label: t.sidebar.tabs.positions },
    { id: 'modelchat' as TabType, label: t.sidebar.tabs.modelChat },
    { id: 'info' as TabType, label: t.sidebar.tabs.readme },
  ];

  return (
    <div className="flex h-full flex-col border-l border-t-0 border-white/10 bg-black sm:border-t-0 sm:border-l">
      {/* Header with close button */}
      <div className="flex items-center justify-between border-b border-white/10 px-2 sm:px-3 py-2">
        <div className="flex-1" />
        {onClose && (
          <button
            onClick={onClose}
            className="flex items-center justify-center w-6 h-6 sm:w-7 sm:h-7 rounded-full border border-white/20 text-gray-400 hover:bg-white/10 hover:text-white transition-colors"
            title="Close sidebar"
          >
            <span className="text-xs sm:text-sm">✕</span>
          </button>
        )}
      </div>
      
      {/* Tab Navigation */}
      <div className="flex border-b border-white/10 overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 min-w-fit border-r border-white/10 px-2 sm:px-3 py-2 sm:py-3 text-[9px] sm:text-[10px] font-mono font-semibold uppercase tracking-wider transition-colors last:border-r-0 whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-white text-black'
                : 'text-gray-400 hover:bg-white/5 hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        {activeTab === 'trades' && (
          <div className="h-full">
            <CompletedTrades />
          </div>
        )}

        {activeTab === 'positions' && (
          <div className="h-full">
            <Positions />
          </div>
        )}

        {activeTab === 'modelchat' && (
          <div className="h-full">
            <TradingLog />
          </div>
        )}

        {activeTab === 'info' && (
          <div className="h-full overflow-y-auto p-3 sm:p-6">
            <div className="space-y-4 sm:space-y-6 font-mono text-[10px] sm:text-xs leading-relaxed">
              <div>
                <h3 className="mb-3 text-sm font-bold">{t.sidebar.readmeContent.betterBenchmark}</h3>
                <p className="text-gray-300">
                  {t.sidebar.readmeContent.description}
                </p>
              </div>

              <div>
                <p className="text-gray-300">
                  {t.sidebar.readmeContent.goal}
                </p>
              </div>

              <div>
                <p className="font-bold text-white">
                  {t.sidebar.readmeContent.ultimate}
                </p>
              </div>

              <div>
                <p className="text-gray-300">
                  {t.sidebar.readmeContent.question}
                </p>
              </div>

              <div className="border-t border-white/10 pt-6">
                <h3 className="mb-3 text-sm font-bold">{t.sidebar.readmeContent.contestants}</h3>
                <div className="space-y-2">
                  <div>
                    <span className="text-blue-400">DeepSeek-V3 Chat</span>
                  </div>
                  <div>
                    <span className="text-purple-400">Doubao (豆包)</span>
                  </div>
                </div>
              </div>

              <div className="border-t border-white/10 pt-6">
                <h3 className="mb-3 text-sm font-bold">{t.sidebar.readmeContent.rules}</h3>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex gap-2">
                    <span className="text-white">└</span>
                    <span>
                      <strong>{t.sidebar.readmeContent.startingCapital}:</strong> {t.sidebar.readmeContent.startingCapitalDesc}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-white">└</span>
                    <span>
                      <strong>{t.sidebar.readmeContent.market}:</strong> {t.sidebar.readmeContent.marketDesc}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-white">└</span>
                    <span>
                      <strong>{t.sidebar.readmeContent.objective}:</strong> {t.sidebar.readmeContent.objectiveDesc}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-white">└</span>
                    <span>
                      <strong>{t.sidebar.readmeContent.transparency}:</strong> {t.sidebar.readmeContent.transparencyDesc}
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-white">└</span>
                    <span>
                      <strong>{t.sidebar.readmeContent.autonomy}:</strong> {t.sidebar.readmeContent.autonomyDesc}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
